import ImageCompressor from "image-compressor.js";
import EXIF from "exif-js";
import {Toast} from "vant";

/**
 * 图片压缩方法（同步回调版）- 未处理IOS苹果手机照片旋转问题
 */
export function compressImageUnhandleOrientation(file, successCallback, errorCallback) {
    // 根据原始文件类型决定输出格式
    const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    // 定制压缩质量
    const quality = typeof file.qualityPercent !== undefined ? file.qualityPercent : 0.6;

    new ImageCompressor(file, {
        quality: quality,
        maxWidth: 800,
        maxHeight: 800,
        mimeType: outputType,
        success: (compressedFile) => {
            // 如果压缩后反而变大，则使用原始文件
            if (compressedFile.size >= file.size) {
                console.warn('压缩后文件未减小，使用原文件');
                successCallback(file);
            } else {
                successCallback(compressedFile);
            }
        },
        error: (err) => {
            console.error('压缩失败，使用原文件:', err);
            errorCallback(err);
        }
    });
}

/**
 * 终极图片压缩方法，彻底解决iOS照片旋转问题
 * @param {File} file - 要压缩的图片文件
 * @param {Function} successCallback - 成功回调函数
 * @param {Function} errorCallback - 失败回调函数
 * @param {Object} [options={}] - 可选配置项
 * @param {boolean} [options.square=false] - 是否压缩为正方形
 * @param {number} [options.quality=0.6] - 压缩质量(0-1)
 * @param {number} [options.maxSize=800] - 最大边长(px)
 */
export function compressImage(file, successCallback, errorCallback, options = {}) {
    const {
        square = false,
        quality = 0.6,
        maxSize = 800
    } = options;

    // 首先读取EXIF方向信息
    EXIF.getData(file, function() {
        const orientation = EXIF.getTag(this, 'Orientation') || 1;
        console.log('检测到的图片方向:', orientation);

        // 根据原始文件类型决定输出格式
        const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';

        // 创建图片对象处理方向问题
        const img = new Image();
        const reader = new FileReader();

        reader.onload = function(e) {
            img.onload = function() {
                // 原始尺寸
                const originalWidth = img.width;
                const originalHeight = img.height;

                // 创建canvas处理方向和尺寸
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                // 确定最终的canvas尺寸
                let canvasWidth, canvasHeight;

                // 计算目标尺寸（考虑是否要正方形）
                if (square) {
                    // 正方形模式，取最小边长
                    const minSide = Math.min(originalWidth, originalHeight, maxSize);
                    canvasWidth = minSide;
                    canvasHeight = minSide;
                } else {
                    // 非正方形模式，保持比例缩放
                    const ratio = Math.min(
                        maxSize / originalWidth,
                        maxSize / originalHeight,
                        1
                    );
                    canvasWidth = Math.floor(originalWidth * ratio);
                    canvasHeight = Math.floor(originalHeight * ratio);
                }

                // 设置canvas尺寸
                canvas.width = canvasWidth;
                canvas.height = canvasHeight;

                // 清空画布
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                // 重置变换矩阵
                ctx.setTransform(1, 0, 0, 1, 0, 0);

                // 计算绘制参数
                let drawWidth, drawHeight, drawX = 0, drawY = 0;

                if (square) {
                    // 正方形模式，居中裁剪
                    const sourceRatio = originalWidth / originalHeight;
                    if (sourceRatio > 1) {
                        // 宽图，裁剪左右
                        drawWidth = originalHeight;
                        drawHeight = originalHeight;
                        drawX = (originalWidth - originalHeight) / 2;
                    } else {
                        // 高图或正方形，裁剪上下
                        drawWidth = originalWidth;
                        drawHeight = originalWidth;
                        drawY = (originalHeight - originalWidth) / 2;
                    }
                } else {
                    // 非正方形模式，保持原比例
                    drawWidth = originalWidth;
                    drawHeight = originalHeight;
                }

                // 根据方向应用变换
                switch(orientation) {
                    case 2: // 水平翻转
                        ctx.translate(canvasWidth, 0);
                        ctx.scale(-1, 1);
                        break;
                    case 3: // 旋转180度
                        ctx.translate(canvasWidth, canvasHeight);
                        ctx.rotate(Math.PI);
                        break;
                    case 4: // 垂直翻转
                        ctx.translate(0, canvasHeight);
                        ctx.scale(1, -1);
                        break;
                    case 5: // 水平翻转 + 旋转90度
                        ctx.rotate(Math.PI/2);
                        ctx.scale(1, -1);
                        ctx.translate(0, -canvasWidth);
                        [canvasWidth, canvasHeight] = [canvasHeight, canvasWidth];
                        break;
                    case 6: // 旋转90度
                        // iPhone照片通常为此方向，但保持正常显示
                        break;
                    case 7: // 水平翻转 + 旋转270度
                        ctx.rotate(-Math.PI/2);
                        ctx.scale(-1, 1);
                        ctx.translate(-canvasHeight, 0);
                        [canvasWidth, canvasHeight] = [canvasHeight, canvasWidth];
                        break;
                    case 8: // 旋转270度
                        ctx.rotate(-Math.PI/2);
                        ctx.translate(-canvasHeight, 0);
                        [canvasWidth, canvasHeight] = [canvasHeight, canvasWidth];
                        break;
                }

                // 绘制图像
                ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight, 0, 0, canvasWidth, canvasHeight);

                // 获取修正后的Blob
                canvas.toBlob(function(blob) {
                    if (!blob) {
                        console.error('Canvas转换Blob失败');
                        errorCallback(new Error('Canvas转换Blob失败'));
                        return;
                    }

                    const compressedFile = new File([blob], file.name, {
                        type: outputType,
                        lastModified: Date.now()
                    });

                    console.log('压缩完成: 原始大小', file.size, '压缩后大小', compressedFile.size);
                    successCallback(compressedFile);
                }, outputType, quality);
            };

            img.onerror = function() {
                console.error('图片加载失败');
                errorCallback(new Error('图片加载失败'));
            };

            img.src = e.target.result;
        };

        reader.onerror = function(err) {
            console.error('文件读取失败:', err);
            errorCallback(err);
        };

        reader.readAsDataURL(file);
    });
}

/**
 * 文件转Base64（同步回调版）
 */
export function fileToBase64(file, successCallback, errorCallback) {
    const reader = new FileReader();
    reader.onload = () => successCallback(reader.result);
    reader.onerror = (error) => {
        console.error('Base64转换失败:', error);
        errorCallback(error);
    };
    reader.onabort = () => errorCallback(new Error('转换被中止'));
    reader.readAsDataURL(file);
}

/**
 * 统一处理上传错误
 */
export function handleUploadError(fileItem, message) {
    fileItem.status = 'failed';
    fileItem.message = message;
    Toast.fail(message);
    console.error('上传错误:', message);
}

/** 图片上传成功后的内容检测提示 */
export const IMAGE_UPLOAD_AUDIT_TIP =
    '若上传的图片涉及违规，平台将不予显示（检测会在1分钟左右完成）';

/**
 * 图片上传成功后的合规提示（可与其它 Toast 同时展示）
 */
export function notifyImageUploadAuditTip() {
    Toast.allowMultiple = true;
    Toast({
        message: IMAGE_UPLOAD_AUDIT_TIP,
        duration: 4000,
    });
}

/**
 * 图片上传最终成功提示（业务文案 + 合规说明）
 */
export function toastImageUploadSuccess(title = '上传成功') {
    Toast({
        message: `${title}\n${IMAGE_UPLOAD_AUDIT_TIP}`,
        duration: 4000,
    });
}

/**
 * 视频压缩方法（使用ffmpeg.js）(无效-废弃)
 */
export function compressVideo(file, successCallback, errorCallback) {
    // 如果没有安装ffmpeg，则直接返回原文件
    if (!window.ffmpeg) {
        console.warn('ffmpeg未加载，使用原始视频');
        successCallback(file);
        return;
    }

    file.message = '压缩中...';

    // 使用ffmpeg压缩（示例配置）
    const ffmpeg = window.ffmpeg;
    ffmpeg.run({
        MEMFS: [{name: 'input.mp4', data: file}],
        arguments: [
            '-i', 'input.mp4',
            '-vf', 'scale=640:-1',  // 缩放宽度为640px，高度自动
            '-c:v', 'libx264',      // H.264编码
            '-crf', '28',           // 质量参数（18-28，值越大压缩率越高）
            '-preset', 'fast',      // 编码速度/压缩率平衡
            '-c:a', 'copy',         // 保留原始音频
            'output.mp4'
        ],
    }).then((results) => {
        const compressedData = results.MEMFS[0].data;
        const compressedFile = new File([compressedData], file.name, {
            type: 'video/mp4'
        });
        successCallback(compressedFile);
    }).catch(errorCallback);
}

/**
 * 统一处理视频上传错误
 */
export function handleVideoUploadError(file, message) {
    file.status = 'failed';
    file.message = message;
    Toast.fail(message);
    console.error('视频上传错误:', message);
}

/**
 * 选取 H5 / iOS Safari 更易播放的 MediaRecorder 编码
 * @returns {string}
 */
export function pickWebCompatibleVideoMimeType() {
    if (typeof MediaRecorder === 'undefined') {
        return '';
    }
    const candidates = [
        'video/mp4; codecs="avc1.42E01E, mp4a.40.2"',
        'video/mp4; codecs=avc1.42E01E,mp4a.40.2',
        'video/mp4',
        'video/webm; codecs=vp8,opus',
        'video/webm',
    ];
    for (let i = 0; i < candidates.length; i += 1) {
        const type = candidates[i];
        if (MediaRecorder.isTypeSupported(type)) {
            return type;
        }
    }
    return '';
}

function isIOSDevice() {
    if (typeof navigator === 'undefined') return false;
    return /iPhone|iPad|iPod/i.test(navigator.userAgent || '') && !window.MSStream;
}

/**
 * 检测本地视频是否可被当前浏览器解码（用于 iOS 上传前校验）
 * @param {File|Blob} file
 * @returns {Promise<boolean>}
 */
export function probeVideoFilePlayable(file) {
    return new Promise((resolve) => {
        if (!(file instanceof Blob)) {
            resolve(false);
            return;
        }
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.playsInline = true;
        video.muted = true;
        const url = URL.createObjectURL(file);
        let settled = false;
        const finish = (ok) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            URL.revokeObjectURL(url);
            video.removeAttribute('src');
            try {
                video.load();
            } catch (e) {
                /* ignore */
            }
            resolve(ok);
        };
        const timer = setTimeout(() => finish(false), 10000);
        video.onloadedmetadata = () => finish(true);
        video.onerror = () => finish(false);
        video.src = url;
    });
}

/**
 * 优化版视频压缩方法（精准进度显示）--- 可将调用前的视频时长判断挪至此处。更简洁
 * MediaRecorder 在部分手机端不稳定；默认移动端跳过压缩。
 * preferWebPlayback=true 时 iOS 会尝试转 H.264 MP4，失败则回退原文件。
 * @param {File} file - 视频文件
 * @param {Function} successCallback - 成功回调
 * @param {Function} errorCallback - 失败回调
 * @param {Object} options - 配置项 { bitrate, mimeType, onProgress, forceCompress, preferWebPlayback }
 */
export function compressWithMediaRecorder(file, successCallback, errorCallback, options = {}) {
    const {
        bitrate = 1000000,
        mimeType = 'video/mp4',
        onProgress = null,
        forceCompress = false,
        preferWebPlayback = false,
    } = options;

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const iosDevice = isIOSDevice();
    const webMime = pickWebCompatibleVideoMimeType();
    const shouldTryCompress =
        forceCompress ||
        (preferWebPlayback && iosDevice && !!webMime) ||
        !isMobile;

    // 移动端默认不压缩；iOS + preferWebPlayback 且支持 MediaRecorder 时尝试转码
    if (isMobile && !shouldTryCompress) {
        if (onProgress) {
            let progress = 0;
            const interval = setInterval(() => {
                progress += 20;
                onProgress(progress);
                if (progress >= 100) {
                    clearInterval(interval);
                    successCallback(file);
                }
            }, 50);
        } else {
            successCallback(file);
        }
        return;
    }

    const recordMime = webMime || (MediaRecorder.isTypeSupported(mimeType) ? mimeType : 'video/webm');
    const video = document.createElement('video');
    video.src = URL.createObjectURL(file);
    video.playsInline = true;
    video.muted = true;
    video.style.display = 'none';
    document.body.appendChild(video);

    // 状态管理
    let mediaRecorder;
    let progressInterval;
    const maxDuration = 100000; // 最大处理时长(ms)
    const cleanup = () => {
        clearTimeout(timeoutTimer);
        clearInterval(progressInterval);
        URL.revokeObjectURL(video.src);
        if (video.parentNode) {
            document.body.removeChild(video);
        }
        if (mediaRecorder?.state === 'recording') {
            mediaRecorder.stop();
        }
    };

    const failOrFallback = (err) => {
        if (preferWebPlayback && iosDevice) {
            cleanup();
            if (onProgress) onProgress(100);
            successCallback(file);
            return;
        }
        cleanup();
        errorCallback(err instanceof Error ? err : new Error(String(err || '视频处理失败')));
    };

    // 超时处理
    const timeoutTimer = setTimeout(() => {
        failOrFallback(new Error('视频处理超时'));
    }, maxDuration);

    // 精准进度计算
    const updateProgress = () => {
        if (!video.duration) return;

        // 计算基于时间的进度 (30% 给准备阶段，70% 给录制阶段)
        const recordingProgress = video.currentTime / video.duration * 70;
        const totalProgress = Math.min(30 + recordingProgress, 99);

        if (onProgress) {
            onProgress(Math.round(totalProgress));
        }
    };

    video.onloadedmetadata = () => {
        if (onProgress) onProgress(10); // 元数据加载完成

        try {
            // 初始化录制器
            const stream = ('captureStream' in video)
                ? video.captureStream()
                : video.mozCaptureStream?.();
            if (!stream) throw new Error('浏览器不支持视频流捕获');

            mediaRecorder = new MediaRecorder(stream, {
                mimeType: recordMime,
                videoBitsPerSecond: bitrate,
            });

            const chunks = [];
            mediaRecorder.ondataavailable = (e) => chunks.push(e.data);

            // 开始进度更新
            progressInterval = setInterval(updateProgress, 200);

            mediaRecorder.onstop = () => {
                cleanup();
                if (onProgress) onProgress(100);
                const blob = new Blob(chunks, { type: mediaRecorder.mimeType || recordMime });
                const outType = blob.type && blob.type.startsWith('video/') ? blob.type : 'video/mp4';
                const baseName = String(file.name || 'video').replace(/\.[^.]+$/, '') || 'video';
                const ext = outType.includes('webm') ? 'webm' : 'mp4';
                successCallback(new File([blob], `${baseName}.${ext}`, { type: outType }));
            };

            mediaRecorder.onerror = (e) => {
                failOrFallback(new Error(`录制错误: ${e.error?.message || '未知错误'}`));
            };

            // 开始处理
            mediaRecorder.start(100);
            video.play().catch((err) => {
                failOrFallback(new Error(`播放失败: ${err.message}`));
            });

        } catch (err) {
            failOrFallback(err);
        }
    };

    video.onerror = () => {
        failOrFallback(new Error('视频加载失败'));
    };
}

export function formatBase64(base64) {
    // 处理三种情况：
    // 1. data:video/[格式];codecs=...;base64, → 转换为 data:video/[格式];base64,
    // 2. data:application/octet-stream;base64, → 转换为 data:video/mp4;base64,
    // 3. 其他格式 → 保持原样（但会被后端拒绝）

    // 处理视频格式（带或不带codecs）
    if (/^data:video\/\w+/i.test(base64)) {
        return base64.replace(
            /(data:video\/\w+);?(codecs=[^;]+;)?(base64,)/i,
            '$1;$3'
        );
    }

    // 处理application/octet-stream格式
    if (/^data:application\/octet-stream;base64,/i.test(base64)) {
        return base64.replace(
            /data:application\/octet-stream;(base64,)/i,
            'data:video/mp4;$1'
        );
    }

    // 其他情况保持原样（会被后端拒绝）
    return base64;
}
